import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';
import { ArrowLeft, MapPin, Users, Briefcase, Calendar, Edit2, X, Save, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import KanbanBoard from './KanbanBoard';
import axios from 'axios';
import toast from 'react-hot-toast';

export default function JobDetail({ job: initialJob, onBack, onPreview }) {
    const [job, setJob] = useState(initialJob);
    const [activeTab, setActiveTab] = useState('pipeline');
    const [matches, setMatches] = useState([]);
    const [loadingMatches, setLoadingMatches] = useState(false);

    // Edit Mode State
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        title: '',
        department: '',
        location: '',
        description: '',
        requirements: ''
    });

    useEffect(() => {
        if (job) {
            setEditForm({
                title: job.title,
                department: job.department,
                location: job.location,
                description: job.description,
                requirements: job.requirements ? job.requirements.join('\n') : ''
            });
        }
    }, [job]);

    if (!job) return null;

    const fetchMatches = async () => {
        setLoadingMatches(true);
        try {
            const res = await axios.post(`${API_BASE}/jobs/${job._id}/match`);
            setMatches(res.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingMatches(false);
        }
    };

    const handleAddCandidate = async (candidate, ai_justification = null) => {
        try {
            await axios.post(`${API_BASE}/jobs/${job._id}/apply`, {
                job_id: job._id,
                resume_id: candidate._id,
                status: 'screening',
                ai_justification: ai_justification
            });
            setMatches(prev => prev.filter(m => m._id !== candidate._id));
            toast.success("Candidate added to pipeline");
        } catch (err) {
            console.error(err);
            toast.error("Failed to add candidate");
        }
    };

    const handleUpdateJob = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...editForm,
                requirements: editForm.requirements.split('\n').filter(r => r.trim())
            };

            await axios.patch(`${API_BASE}/jobs/${job._id}`, payload);
            toast.success("Job updated successfully");
            setJob(prev => ({ ...prev, ...payload }));
            setIsEditing(false);
        } catch (err) {
            console.error(err);
            toast.error("Failed to update job");
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6 animate-fade-in pb-20">
            <div className="flex justify-between items-center">
                <button
                    onClick={onBack}
                    className="flex items-center text-slate-500 hover:text-brand-600 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back to Jobs
                </button>
                <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-brand-600 dark:hover:text-brand-400 bg-white dark:bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm transition-colors"
                >
                    <Edit2 className="w-4 h-4" /> Edit Job
                </button>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="p-8 border-b border-slate-100 dark:border-slate-700">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 rounded-xl">
                                <Briefcase className="w-8 h-8" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{job.title}</h1>
                                <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 dark:text-slate-400">
                                    <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {job.department}</span>
                                    <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {job.location}</span>
                                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Posted {new Date(job.created_at).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold capitalize ${job.status === 'open'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                            }`}>
                            {job.status}
                        </span>
                    </div>
                </div>

                <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-8">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-3">About the Role</h3>
                            <p className="text-slate-600 dark:text-slate-300 whitespace-pre-line leading-relaxed">
                                {job.description}
                            </p>
                        </div>

                        {job.requirements && job.requirements.length > 0 && (
                            <div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-3">Requirements</h3>
                                <ul className="space-y-2">
                                    {job.requirements.map((req, i) => (
                                        <li key={i} className="flex items-start gap-2 text-slate-600 dark:text-slate-300">
                                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0" />
                                            <span>{req}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-1 border-l border-slate-100 dark:border-slate-800 lg:pl-8">
                        <div className="relative overflow-hidden p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg group bg-white/50 dark:bg-slate-800/50 backdrop-blur-md">
                            <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent dark:from-white/5 dark:to-transparent opacity-50" />

                            <div className="relative z-10 w-12 h-12 mx-auto bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full flex items-center justify-center mb-3 shadow-inner">
                                <span className="text-xl">✨</span>
                            </div>

                            <h3 className="relative z-10 font-bold text-slate-900 dark:text-white mb-2">Automated Sourcing</h3>
                            <p className="relative z-10 text-sm text-slate-500 dark:text-slate-400 mb-6">Let AI find the best candidates from your database.</p>

                            <button
                                onClick={() => { setActiveTab('match'); fetchMatches(); }}
                                className="relative z-10 w-full py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg font-medium transition-all duration-300 hover:bg-purple-600 hover:text-white dark:hover:bg-purple-600 dark:hover:text-white shadow-md hover:shadow-purple-500/25"
                            >
                                Run Smart Match
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-8 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/30">
                    <div className="flex gap-4 mb-6 border-b border-slate-200 dark:border-slate-700">
                        <button
                            onClick={() => setActiveTab('pipeline')}
                            className={`pb-3 px-1 font-medium text-sm transition-colors relative ${activeTab === 'pipeline' ? 'text-brand-600 dark:text-brand-400' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'}`}
                        >
                            Pipeline
                            {activeTab === 'pipeline' && <motion.div layoutId="underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-600" />}
                        </button>
                        <button
                            onClick={() => { setActiveTab('match'); fetchMatches(); }}
                            className={`pb-3 px-1 font-medium text-sm transition-colors relative ${activeTab === 'match' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'}`}
                        >
                            Smart Match
                            {activeTab === 'match' && <motion.div layoutId="underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" />}
                        </button>
                    </div>

                    {activeTab === 'pipeline' ? (
                        <div className="h-[500px] border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 overflow-hidden">
                            <KanbanBoard jobId={job._id} onPreview={onPreview} />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {loadingMatches && <div className="col-span-full py-10 text-center text-slate-500">Scanning database...</div>}
                            {!loadingMatches && matches.length === 0 && <div className="col-span-full py-10 text-center text-slate-500">No new matches found.</div>}

                            {/* Safe iteration */}
                            {(matches || []).map(candidate => (
                                <MatchCard
                                    key={candidate._id}
                                    candidate={candidate}
                                    jobId={job._id}
                                    onPreview={onPreview}
                                    onAdd={(cand, just) => handleAddCandidate(cand, just)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Edit Job Modal */}
            <AnimatePresence>
                {isEditing && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
                        onClick={() => setIsEditing(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                            className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
                                <h3 className="text-xl font-bold text-slate-800 dark:text-white">Edit Job</h3>
                                <button onClick={() => setIsEditing(false)} className="text-slate-400 hover:text-red-500"><X className="w-5 h-5" /></button>
                            </div>

                            <form onSubmit={handleUpdateJob} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Job Title</label>
                                        <input
                                            required
                                            className="w-full p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                                            value={editForm.title}
                                            onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Department</label>
                                        <input
                                            className="w-full p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                                            value={editForm.department}
                                            onChange={e => setEditForm({ ...editForm, department: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Location</label>
                                    <input
                                        className="w-full p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                                        value={editForm.location}
                                        onChange={e => setEditForm({ ...editForm, location: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
                                    <textarea
                                        required
                                        className="w-full p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none min-h-[100px]"
                                        value={editForm.description}
                                        onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Key Requirements (one per line)</label>
                                    <textarea
                                        className="w-full p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none min-h-[100px]"
                                        value={editForm.requirements}
                                        onChange={e => setEditForm({ ...editForm, requirements: e.target.value })}
                                    />
                                </div>
                            </form>

                            <div className="p-6 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 bg-slate-50 dark:bg-slate-900/50">
                                <button
                                    onClick={() => setIsEditing(false)}
                                    className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateJob}
                                    className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-bold flex items-center gap-2 shadow-lg shadow-brand-200 dark:shadow-none"
                                >
                                    <Save className="w-4 h-4" /> Save Changes
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function MatchCard({ candidate, jobId, onPreview, onAdd }) {
    const [justification, setJustification] = useState(null);
    const [loadingJust, setLoadingJust] = useState(false);

    const fetchJustification = async (e) => {
        e.stopPropagation();
        if (justification) return;
        setLoadingJust(true);
        try {
            const res = await axios.get(`${API_BASE}/jobs/${jobId}/candidates/${candidate._id}/justification`);
            setJustification(res.data.justification);
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch justification");
        } finally {
            setLoadingJust(false);
        }
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            key={candidate._id}
            onClick={() => onPreview(candidate._id, candidate.name, { ...candidate, fitReason: justification })}
            className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col cursor-pointer hover:shadow-md hover:border-brand-200 dark:hover:border-brand-800 transition-all group"
        >
            <div className="flex justify-between items-start mb-3">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-slate-800 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors uppercase tracking-tight">{candidate.name}</h4>
                        <span className="text-xs font-mono bg-green-100 text-green-700 px-1.5 py-0.5 rounded border border-green-200">
                            {Math.round(candidate.match_score * 100)}% Match
                        </span>
                    </div>
                    <p className="text-[11px] text-slate-500">{candidate.years_experience}y exp • {candidate.skills.slice(0, 3).join(', ')}</p>
                </div>
                <button
                    onClick={(e) => { e.stopPropagation(); onAdd(candidate, justification); }}
                    className="text-[10px] bg-slate-900 text-white px-3 py-1.5 rounded-lg hover:bg-brand-600 transition-colors shadow-sm font-bold uppercase tracking-widest"
                >
                    Add to Pipeline
                </button>
            </div>

            <div className="mt-2 pt-3 border-t border-slate-50 dark:border-slate-700/50">
                {!justification ? (
                    <button
                        onClick={fetchJustification}
                        className="text-[10px] text-brand-600 dark:text-brand-400 font-bold flex items-center gap-1 hover:underline"
                    >
                        {loadingJust ? <Loader2 className="w-3 h-3 animate-spin" /> : '✨ Why this match?'}
                    </button>
                ) : (
                    <div className="text-[11px] text-slate-600 dark:text-slate-400 italic bg-slate-50 dark:bg-slate-900/50 p-2 rounded-lg border border-slate-100 dark:border-slate-800">
                        " {justification} "
                    </div>
                )}
            </div>
        </motion.div>
    );
}
